import React, { useEffect, useState, useRef } from "react";
import { useRecurly } from "@recurly/react-recurly";
import { PayPalConfig } from "@recurly/recurly-js";

export interface RecurlyToken {
  id: string;
}

interface PayPalButtonProps {
  onToken: (token: RecurlyToken) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  isDisabled?: boolean;
}

/**
 * PayPal Button using Recurly's PayPal Complete integration
 * Docs: https://docs.recurly.com/docs/paypal#paypal-complete
 */
const PayPalButton: React.FC<PayPalButtonProps> = ({ onToken, onError, onCancel, isDisabled = false }) => {
  const recurly = useRecurly();
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const initializedRef = useRef(false);

  // Store callbacks in refs to avoid re-initialization when they change
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const onCancelRef = useRef(onCancel);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onTokenRef.current = onToken;
    onErrorRef.current = onError;
    onCancelRef.current = onCancel;
  }, [onToken, onError, onCancel]);

  // Initialize PayPal Complete instance on mount (only once)
  useEffect(() => {
    if (isDisabled || initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    try {
      const paypal = recurly.PayPal({
        payPalComplete: {
          target: "#paypal-button",
          buttonOptions: {},
        },
      } as PayPalConfig);

      paypal.on("error", (err) => {
        const errorObj = err as { code?: string; message?: string; cause?: { message?: string } };
        const displayError = errorObj?.cause?.message || errorObj?.message || "PayPal error";
        setError(displayError);
        onErrorRef.current?.(displayError);
      });

      paypal.on("token", (token) => {
        onTokenRef.current(token as RecurlyToken);
      });

      paypal.on("cancel", () => {
        onCancelRef.current?.();
      });

      setIsReady(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to initialize PayPal.";
      setError(errorMessage);
      onErrorRef.current?.(errorMessage);
    }
  }, [recurly, isDisabled]);

  if (isDisabled) {
    return null;
  }

  return (
    <div className="paypal-button-container">
      <div id="paypal-button" className="min-h-[44px]" style={{ opacity: isReady ? 1 : 0.6 }} />

      {error && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PayPalButton;
